import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';

type ScheduledPost = {
  id: string;
  table: 'wall_posts' | 'group_posts';
  content: string | null;
  status: string | null;
  scheduled_for: string | null;
  target: string;
};

type MessageState = { type: 'success' | 'error'; text: string } | null;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const Subtitle = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`;

const SectionHeading = styled.h2`
  margin: 1rem 0 0.5rem;
  font-size: 1.1rem;
  color: var(--onSurface);

  &:first-child {
    margin-top: 0;
  }
`;

const PostItem = styled.div`
  border: 1px solid var(--outlineVariant);
  border-radius: 12px;
  padding: 0.95rem;
  background: var(--surfaceVariant);
  margin-bottom: 0.75rem;
`;

const PostContent = styled.div`
  color: var(--onSurface);
  margin-bottom: 0.5rem;
  white-space: pre-wrap;
`;

const MetaText = styled.p`
  margin: 0 0 0.6rem;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  align-items: center;
`;

const RescheduleInput = styled.input`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.4rem 0.6rem;
  font: inherit;
  color: var(--onSurface);
  background: var(--surface);
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 0.55rem 0.9rem;
  font: inherit;
  font-weight: 600;
  background: var(--primary);
  color: var(--onPrimary);
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(ActionButton)`
  background: transparent;
  border: 1px solid var(--error);
  color: var(--error);
`;

const Message = styled.div<{ $type: 'success' | 'error' }>`
  border-radius: 12px;
  padding: 0.85rem 1rem;
  font-size: 0.95rem;
  border: 1px solid ${({ $type }) => ($type === 'success' ? 'var(--primary)' : 'var(--error)')};
  background: ${({ $type }) => ($type === 'success' ? 'rgba(76, 175, 80, 0.12)' : 'rgba(244, 67, 54, 0.12)')};
  color: ${({ $type }) => ($type === 'success' ? 'var(--onSurface)' : 'var(--error)')};
`;

const EmptyState = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
`;

const ScheduledPostsPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [message, setMessage] = useState<MessageState>(null);
  const [reschedulingId, setReschedulingId] = useState<string | null>(null);
  const [draftTimes, setDraftTimes] = useState<Record<string, string>>({});

  const showMessage = useCallback((type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    window.setTimeout(() => setMessage(null), 3000);
  }, []);

  const loadScheduledPosts = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [wallRes, groupRes] = await Promise.all([
        supabase
          .from('wall_posts')
          .select('id, content, status, scheduled_for')
          .eq('author_id', user.id)
          .in('status', ['scheduled', 'pending'])
          .not('scheduled_for', 'is', null)
          .order('scheduled_for', { ascending: true }),
        supabase
          .from('group_posts')
          .select('id, content, status, scheduled_for, group_id')
          .eq('author_id', user.id)
          .in('status', ['scheduled', 'pending'])
          .not('scheduled_for', 'is', null)
          .order('scheduled_for', { ascending: true }),
      ]);

      if (wallRes.error || groupRes.error) {
        showMessage('error', 'Failed to load scheduled posts.');
        return;
      }

      const groupPosts = (groupRes.data ?? []) as Array<{
        id: string;
        content: string | null;
        status: string | null;
        scheduled_for: string | null;
        group_id: string;
      }>;

      const groupIds = Array.from(new Set(groupPosts.map((post) => post.group_id).filter(Boolean)));
      const groupNamesById = new Map<string, string>();
      if (groupIds.length > 0) {
        const { data: groups } = await supabase.from('groups').select('id, name').in('id', groupIds);
        (groups ?? []).forEach((group: any) => groupNamesById.set(group.id, group.name || 'Group'));
      }

      const wallPosts: ScheduledPost[] = (wallRes.data ?? []).map((post: any) => ({
        id: post.id,
        table: 'wall_posts',
        content: post.content,
        status: post.status,
        scheduled_for: post.scheduled_for,
        target: 'Main Feed',
      }));

      const mappedGroupPosts: ScheduledPost[] = groupPosts.map((post) => ({
        id: post.id,
        table: 'group_posts',
        content: post.content,
        status: post.status,
        scheduled_for: post.scheduled_for,
        target: groupNamesById.get(post.group_id) || 'Group',
      }));

      setPosts([...wallPosts, ...mappedGroupPosts]);
    } catch (error) {
      showMessage('error', 'Failed to load scheduled posts.');
    } finally {
      setLoading(false);
    }
  }, [user?.id, showMessage]);

  useEffect(() => {
    loadScheduledPosts();
  }, [loadScheduledPosts]);

  const handleCancel = useCallback(
    async (post: ScheduledPost) => {
      if (!window.confirm('Cancel and delete this scheduled post?')) return;
      const { error } = await supabase.from(post.table).delete().eq('id', post.id);
      if (error) {
        showMessage('error', 'Unable to cancel this post.');
        return;
      }
      setPosts((current) => current.filter((item) => item.id !== post.id));
      showMessage('success', 'Scheduled post cancelled.');
    },
    [showMessage]
  );

  const handleReschedule = useCallback(
    async (post: ScheduledPost) => {
      const draft = draftTimes[post.id];
      if (!draft || new Date(draft).getTime() <= Date.now()) {
        showMessage('error', 'Please choose a date and time in the future.');
        return;
      }

      setReschedulingId(post.id);
      const scheduledForIso = new Date(draft).toISOString();
      const { error } = await supabase
        .from(post.table)
        .update({ scheduled_for: scheduledForIso })
        .eq('id', post.id);
      setReschedulingId(null);

      if (error) {
        showMessage('error', 'Unable to reschedule this post.');
        return;
      }

      setPosts((current) =>
        current.map((item) => (item.id === post.id ? { ...item, scheduled_for: scheduledForIso } : item))
      );
      showMessage('success', 'Post rescheduled.');
    },
    [draftTimes, showMessage]
  );

  const mainFeedPosts = posts.filter((post) => post.table === 'wall_posts');
  const groupPostsByTarget = posts
    .filter((post) => post.table === 'group_posts')
    .reduce<Record<string, ScheduledPost[]>>((acc, post) => {
      acc[post.target] = acc[post.target] ?? [];
      acc[post.target].push(post);
      return acc;
    }, {});

  const renderPost = (post: ScheduledPost) => (
    <PostItem key={post.id}>
      <PostContent>{post.content || 'No content'}</PostContent>
      <MetaText>
        {post.status === 'pending'
          ? 'Awaiting approval before it can be scheduled to publish'
          : `Publishes on ${post.scheduled_for ? new Date(post.scheduled_for).toLocaleString() : 'unknown'}`}
      </MetaText>
      <ActionRow>
        <RescheduleInput
          type="datetime-local"
          disabled={post.status === 'pending'}
          value={draftTimes[post.id] ?? (post.scheduled_for ? post.scheduled_for.slice(0, 16) : '')}
          min={new Date().toISOString().slice(0, 16)}
          onChange={(event) => setDraftTimes((current) => ({ ...current, [post.id]: event.target.value }))}
        />
        <ActionButton
          type="button"
          disabled={post.status === 'pending' || reschedulingId === post.id}
          onClick={() => handleReschedule(post)}
        >
          {reschedulingId === post.id ? 'Saving...' : 'Reschedule'}
        </ActionButton>
        <DangerButton type="button" onClick={() => handleCancel(post)}>
          Cancel
        </DangerButton>
      </ActionRow>
    </PostItem>
  );

  return (
    <Page>
      <PageTitle>Scheduled Posts</PageTitle>
      <Subtitle>Posts you've scheduled for the main feed and your groups.</Subtitle>
      {message ? <Message $type={message.type}>{message.text}</Message> : null}
      <Card>
        {loading ? (
          <EmptyState>Loading...</EmptyState>
        ) : posts.length === 0 ? (
          <EmptyState>You have no scheduled posts.</EmptyState>
        ) : (
          <>
            {mainFeedPosts.length > 0 ? (
              <>
                <SectionHeading>Main Feed</SectionHeading>
                {mainFeedPosts.map(renderPost)}
              </>
            ) : null}
            {Object.entries(groupPostsByTarget).map(([groupName, groupPosts]) => (
              <React.Fragment key={groupName}>
                <SectionHeading>{groupName}</SectionHeading>
                {groupPosts.map(renderPost)}
              </React.Fragment>
            ))}
          </>
        )}
      </Card>
    </Page>
  );
};

export default ScheduledPostsPage;
